import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getResearchReport, type ResearchDetail } from "@/api/research";
import { sendChatMessage, createChatConversation } from "@/api/chat";
import type { ChatIntent } from "@/api/chat";
import ActionButtons, { stripActions } from "@/components/feature/ActionButtons";

function MiniChart({ data, color = "#6366f1", height = 60 }: { data: number[]; color?: string; height?: number }) {
  const min = Math.min(...data); const max = Math.max(...data); const range = max - min || 1;
  const w = data.length * 24;
  const pts = data.map((v, i) => `${i * 24},${height - ((v - min) / range) * (height - 8) - 4}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${height}`} className="w-full h-full" preserveAspectRatio="none">
      <defs><linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity="0.2" /><stop offset="100%" stopColor={color} stopOpacity="0.02" /></linearGradient></defs>
      <polygon points={`0,${height} ${pts} ${w},${height}`} fill="url(#chartFill)" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function prefSummary(pref: string): string {
  if (!pref) return "";
  const m: Record<string, string> = {};
  pref.split(/[，,、]/).forEach(p => {
    const [k, ...v] = p.split(/[:：]/);
    if (k && v.length) m[k.trim()] = v.join(":").trim();
  });
  const parts: string[] = [];
  if (m["purpose"]) parts.push(m["purpose"]);
  if (m["direction"]) parts.push(m["direction"]);
  if (m["horizon"]) parts.push(m["horizon"].replace(/[（(].*[）)]/, ""));
  if (m["focus"]) parts.push(m["focus"]);
  if (m["risk"]) parts.push(m["risk"]);
  if (m["valuation"]) parts.push(m["valuation"].replace(/[（(].*[）)]/, ""));
  return parts.length ? parts.join(" · ") : "";
}

function DonutChart({ segments }: { segments: { name: string; value: number; color: string }[] }) {
  const total = segments.reduce((s, x) => s + x.value, 0);
  let acc = 0;
  const arcs = segments.map(s => {
    const p = s.value / total * 360;
    const start = acc; acc += p; const end = acc;
    const sr = ((start - 90) * Math.PI) / 180, er = ((end - 90) * Math.PI) / 180;
    const r = 40;
    const x1 = 50 + r * Math.cos(sr), y1 = 50 + r * Math.sin(sr);
    const x2 = 50 + r * Math.cos(er), y2 = 50 + r * Math.sin(er);
    const large = p > 180 ? 1 : 0;
    return { d: `M50,50 L${x1},${y1} A${r},${r} 0 ${large} 1 ${x2},${y2} Z`, fill: s.color, label: s.name, value: s.value };
  });
  return (
    <svg viewBox="0 0 100 100" className="w-full max-w-[200px] mx-auto">
      {arcs.map((a, i) => <path key={i} d={a.d} fill={a.fill} />)}
      <circle cx="50" cy="50" r="22" fill="white" />
    </svg>
  );
}

export default function ResearchReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<ResearchDetail | null>(null);
  const [wacc, setWacc] = useState(8.5);
  const [growth, setGrowth] = useState(3.0);
  const [showChat, setShowChat] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [chatMsgs, setChatMsgs] = useState<{ role: string; content: string }[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatInited, setChatInited] = useState(false);
  const [chatConvId, setChatConvId] = useState(0);
  const [chatIntent, setChatIntent] = useState<ChatIntent | null>(null);
  const [toast, setToast] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!id) return;
    getResearchReport(Number(id))
      .then((r) => {
        setReport(r);
        let d: ResearchDetail | null = null;
        if (r.detail) {
          d = typeof r.detail === "string" ? JSON.parse(r.detail as any) : r.detail;
          setDetail(d);
          setWacc(d.dcf?.wacc || 8.5);
          setGrowth(d.dcf?.perpetuityGrowth || 3.0);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  // ── Poll every 3s while report is still generating ──
  useEffect(() => {
    if (!id || !report) return;
    if (report.status !== "生成中" && report.detail) return;
    const timer = setInterval(() => {
      getResearchReport(Number(id))
        .then((r) => {
          setReport(r);
          if (r.detail) {
            const d = typeof r.detail === "string" ? JSON.parse(r.detail as any) : r.detail;
            setDetail(d);
            setWacc(d.dcf?.wacc || 8.5);
            setGrowth(d.dcf?.perpetuityGrowth || 3.0);
          }
        })
        .catch(() => {});
    }, 3_000);
    return () => clearInterval(timer);
  }, [id, report?.status]);

  // ── Auto-clear toast ──
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  const calcValue = detail ? detail.dcf.intrinsicValue * (1 + (growth - detail.dcf.perpetuityGrowth) / 10) / (1 + (wacc - detail.dcf.wacc) / 20) : 0;

  const handleChatSend = async (text?: string) => {
    const q = (text || chatInput).trim();
    if (!q || chatLoading) return;
    setChatInput("");
    setChatLoading(true);
    setChatIntent(null);

    let cid = chatConvId;
    if (cid === 0) {
      try {
        const r = await createChatConversation(`研报追问: ${report?.stockName || `#${id}`}`);
        const conv = (r as any)?.data || r;
        cid = conv.id;
        setChatConvId(cid);
      } catch { /* non-fatal */ }
    }

    const userMsg = { role: "user" as const, content: q };
    setChatMsgs(prev => [...prev, userMsg]);
    const aiId = Date.now();
    setChatMsgs(prev => [...prev, { role: "ai" as const, content: "", id: aiId }]);

    let msgs: any[] = [];
    if (!chatInited && report) {
      setChatInited(true);
      const summary = report.summary || "";
      const rating = report.rating || "";
      const name = report.stockName || "";
      msgs.push({ role: "system", content: `你是 天鼎AI 的 AI 研报助手，天鼎AI 是「阿尔法工坊」的升级版，集成了模拟盘、策略、研报等全部功能。正在为用户解读${name}的股权研究报告。研报评级：${rating}，核心摘要：${summary}。请基于报告内容回答用户问题。如果用户问其他股票，可以从数据库中搜索该股票的行情数据进行分析。始终注明"仅供学习参考，不构成投资建议"。

若用户想生成其他股票的研报，回复末尾附加[去研报生成]。若用户想查看研报库、策略、持仓等，附加对应的跳转标记：[去AI 看盘] [去研报生成] [去研报库] [去策略] [去持仓] [去订阅]。` });
    }
    for (const m of chatMsgs) {
      if (m.role === "user" || m.role === "ai") {
        msgs.push({ role: m.role === "ai" ? "assistant" : "user", content: m.content });
      }
    }
    msgs.push({ role: "user", content: q });

    try {
      let acc = "";
      for await (const delta of sendChatMessage(msgs, null, cid || undefined, {
        onIntent: (intent) => setChatIntent(intent),
      })) {
        acc += delta;
        setChatMsgs(prev => prev.map((m) => (m.id === aiId ? { ...m, content: acc } : m)));
      }
      setChatMsgs(prev => prev.map((m) => (m.id === aiId ? { ...m, content: acc || "暂时无法回答，请稍后再试。" } : m)));
    } catch {
      setChatMsgs(prev => prev.map((m) => (m.id === aiId ? { ...m, content: "AI 暂时无法响应。" } : m)));
    } finally {
      setChatLoading(false);
    }
  };

  if (loading) return <div className="flex-1 flex items-center justify-center"><div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!report) return <div className="flex-1 px-4 py-4"><p className="text-foreground-400 text-sm text-center py-12">研报未找到</p></div>;

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4" ref={scrollRef}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => navigate(-1)} className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center cursor-pointer"><i className="ri-arrow-left-s-line text-foreground-600 text-lg" /></button>
      </div>

      {/* ═══ 封面摘要 ═══ */}
      <div className="bg-white rounded-xl p-4 border border-background-200/60 mb-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h1 className="text-xl font-bold text-foreground-950">{report.stockName}<span className="text-sm text-foreground-400 ml-2">{report.stockCode}</span></h1>
            <span className="text-xs text-foreground-400">🇨🇳 {report.industry} · {report.createdAt ? new Date(report.createdAt * 1000).toLocaleDateString() : ""}</span>
          </div>
          <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold inline-flex items-center gap-1"
            style={{ color: "#E11D48", background: "rgba(225,29,72,0.1)" }}>{report.rating || "--"}
            {!report.detail && <span className="w-2.5 h-2.5 border-2 border-current border-t-transparent rounded-full animate-spin" />}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-3 my-3">
          <div className="bg-background-50 rounded-lg p-2.5 text-center"><div className="text-sm font-bold text-foreground-950">{report.targetPrice ? `¥${report.targetPrice}` : "--"}</div><div className="text-[9px] text-foreground-400">目标价</div></div>
          <div className="bg-background-50 rounded-lg p-2.5 text-center"><div className="text-sm font-bold text-foreground-950">{report.currentPrice ? `¥${report.currentPrice}` : "--"}</div><div className="text-[9px] text-foreground-400">现价</div></div>
          <div className="bg-background-50 rounded-lg p-2.5 text-center"><div className="text-sm font-bold text-[#E11D48]">{report.upside ? `${report.upside.toFixed(1)}%` : "--"}</div><div className="text-[9px] text-foreground-400">上行空间</div></div>
        </div>
        {report.summary && <p className="text-xs text-foreground-600 leading-relaxed">{report.summary}</p>}
        {detail?.summary?.map((s, i) => <p key={i} className="text-xs text-foreground-600 mt-2 leading-relaxed">• {s}</p>)}
        <div className="text-[10px] text-foreground-400 mt-3">报告日期：{detail?.reportDate || "--"} · 数据截止：{detail?.dataCutoff || "--"}</div>
        <div className="text-[9px] text-foreground-300 mt-1">本报告由 AI 自动生成，不构成投资建议</div>
      </div>

      {/* ═══ 公司概览 ═══ */}
      {detail?.business && (
        <div className="bg-white rounded-xl p-4 border border-background-200/60 mb-4">
          <h2 className="text-base font-bold text-foreground-950 mb-3">公司与行业概览</h2>
          <p className="text-[11px] text-foreground-600 mb-3">{detail.business.industryPosition}</p>
          <h3 className="text-xs font-semibold text-foreground-950 mb-2">主营业务构成</h3>
          <DonutChart segments={detail.business.segments} />
          <div className="flex flex-wrap justify-center gap-2 mt-2 mb-4">
            {detail.business.segments.map(s => (
              <div key={s.name} className="flex items-center gap-1 text-[10px]">
                <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                <span className="text-foreground-600">{s.name} {s.value}%</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-foreground-600 mt-3">{detail.business.industryOutlook}</p>
        </div>
      )}

      {/* ═══ 财务分析 ═══ */}
      {detail?.financials && (
        <div className="bg-white rounded-xl p-4 border border-background-200/60 mb-4">
          <h2 className="text-base font-bold text-foreground-950 mb-3">财务三表分析</h2>
          <div className="space-y-5">
            <div>
              <div className="flex items-center justify-between mb-1"><span className="text-xs font-semibold text-foreground-950">营收趋势</span><span className="text-[9px] text-foreground-400">亿元</span></div>
              <div className="h-16"><MiniChart data={detail.financials.revenue} color="#6366f1" height={60} /></div>
              <div className="flex justify-between text-[9px] text-foreground-400 mt-0.5">{detail.financials.years.map(y => <span key={y}>{y}</span>)}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[{ label: "营收", data: detail.financials.revenue, color: "#6366f1" }, { label: "净利", data: detail.financials.netProfit, color: "#10B981" }, { label: "毛利率%", data: detail.financials.grossMargin, color: "#F59E0B" }, { label: "ROE%", data: detail.financials.roe, color: "#E11D48" }].map(m => (
                <div key={m.label} className="bg-background-50 rounded-lg p-2.5">
                  <div className="text-[9px] text-foreground-400 mb-1">{m.label}</div>
                  <div className="h-10"><MiniChart data={m.data} color={m.color} height={36} /></div>
                </div>
              ))}
            </div>
            <table className="w-full text-[10px]">
              <thead><tr className="text-foreground-400 border-b border-background-100"><th className="text-left py-1">年份</th>{detail.financials.years.map(y => <th key={y} className="text-right py-1">{y}</th>)}</tr></thead>
              <tbody>
                {[{ label: "营收", data: detail.financials.revenue }, { label: "净利", data: detail.financials.netProfit }, { label: "毛利率%", data: detail.financials.grossMargin }, { label: "ROE%", data: detail.financials.roe }].map(r => (
                  <tr key={r.label} className="border-b border-background-50"><td className="text-foreground-600 py-1.5">{r.label}</td>{r.data.map((v, i) => <td key={i} className="text-right text-foreground-950 font-medium py-1.5">{typeof v === "number" ? v.toFixed(1) : v}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ 盈利预测 ═══ */}
      {detail?.forecast && (
        <div className="bg-white rounded-xl p-4 border border-background-200/60 mb-4">
          <h2 className="text-base font-bold text-foreground-950 mb-3">营收与盈利预测</h2>
          <div className="space-y-4">
            <div><div className="flex items-center justify-between mb-1"><span className="text-xs font-semibold text-foreground-950">营收预测</span><span className="text-[9px] text-foreground-400">亿元</span></div>
              <div className="h-14"><MiniChart data={detail.forecast.revenue} color="#6366f1" height={48} /></div>
              <div className="flex justify-between text-[9px] text-foreground-400 mt-0.5">{detail.forecast.years.map(y => <span key={y}>{y}</span>)}</div>
            </div>
            <table className="w-full text-[10px]">
              <thead><tr className="text-foreground-400 border-b border-background-100"><th className="text-left py-1">指标</th>{detail.forecast.years.map(y => <th key={y} className="text-right py-1">{y}</th>)}</tr></thead>
              <tbody>
                {[{ label: "营收", data: detail.forecast.revenue }, { label: "净利", data: detail.forecast.netProfit }, { label: "EBITDA", data: detail.forecast.ebitda }].map(r => (
                  <tr key={r.label} className="border-b border-background-50"><td className="text-foreground-600 py-1.5">{r.label}</td>{r.data.map((v, i) => <td key={i} className="text-right text-foreground-950 font-medium py-1.5">{v.toFixed(1)}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ DCF 估值 ═══ */}
      {detail?.dcf && (
        <div className="bg-white rounded-xl p-4 border border-background-200/60 mb-4">
          <h2 className="text-base font-bold text-foreground-950 mb-3">DCF 估值</h2>
          <p className="text-[10px] text-foreground-400 mb-4">调整假设参数，估值将实时更新</p>
          <div className="space-y-3 mb-4">
            <div><div className="flex justify-between text-xs mb-1"><span className="text-foreground-600">WACC</span><span className="font-semibold text-foreground-950">{wacc}%</span></div>
              <input type="range" min="6" max="12" step="0.1" value={wacc} onChange={e => setWacc(Number(e.target.value))} className="w-full accent-indigo-500" />
              <div className="flex justify-between text-[9px] text-foreground-300"><span>6%</span><span>12%</span></div></div>
            <div><div className="flex justify-between text-xs mb-1"><span className="text-foreground-600">永续增长率</span><span className="font-semibold text-foreground-950">{growth}%</span></div>
              <input type="range" min="1" max="5" step="0.1" value={growth} onChange={e => setGrowth(Number(e.target.value))} className="w-full accent-indigo-500" />
              <div className="flex justify-between text-[9px] text-foreground-300"><span>1%</span><span>5%</span></div></div>
          </div>
          <div className="text-center mb-4">
            <div className="text-[10px] text-foreground-400">每股内在价值</div>
            <div className="text-3xl font-bold text-foreground-950">¥{calcValue.toFixed(2)}</div>
          </div>
          <div className="bg-background-50 rounded-xl p-3">
            <div className="text-[10px] font-semibold text-foreground-600 mb-2 text-center">敏感性矩阵</div>
            <div className="overflow-x-auto">
              <table className="w-full text-center text-[10px] min-w-[250px]">
                <thead><tr><th className="text-foreground-400 pb-1 font-normal">WACC \ 增长</th>{detail.dcf.sensitivity.growthValues.map(g => <th key={g} className="text-foreground-400 pb-1 font-normal">{g}%</th>)}</tr></thead>
                <tbody>{detail.dcf.sensitivity.waccValues.map((w, i) => (
                  <tr key={w}><td className="text-foreground-400 pr-1 text-left text-[9px]">{w}%</td>
                    {detail.dcf.sensitivity.matrix[i]?.map((v, j) => {
                      const baseVal = calcValue || detail.dcf.intrinsicValue;
                      const intensity = Math.max(0.05, Math.min(0.5, 0.5 - Math.abs(v - baseVal) / baseVal / 2));
                      return <td key={j} className="font-semibold text-foreground-950 py-0.5 px-1 rounded" style={{ background: `rgba(99,102,241,${intensity})` }}>{Math.round(v)}</td>;
                    })}
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
          <p className="text-[9px] text-foreground-400 mt-2">DCF 为基于假设的模型测算，非确定性估值</p>
        </div>
      )}

      {/* ═══ 相对估值 ═══ */}
      {detail?.relativeValuation && (
        <div className="bg-white rounded-xl p-4 border border-background-200/60 mb-4">
          <h2 className="text-base font-bold text-foreground-950 mb-3">相对估值与同业对比</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "当前 PE", val: detail.relativeValuation.peCurrent, pct: true },
                { label: "历史中位 PE", val: detail.relativeValuation.peHistoricalP50, pct: true },
                { label: "同业中位 PE", val: detail.relativeValuation.pePeerMedian, pct: true },
                { label: "当前 PB", val: detail.relativeValuation.pbCurrent },
                { label: "历史中位 PB", val: detail.relativeValuation.pbHistoricalP50 },
                { label: "同业中位 PB", val: detail.relativeValuation.pbPeerMedian },
              ].map(m => (
                <div key={m.label} className="bg-background-50 rounded-lg p-2.5 text-center">
                  <div className="text-[9px] text-foreground-400">{m.label}</div>
                  <div className="text-sm font-bold text-foreground-950">{typeof m.val === "number" ? m.val.toFixed(2) : "--"}</div>
                </div>
              ))}
            </div>
            <div><div className="text-xs font-semibold text-foreground-950 mb-2">同业对比</div>
              <div className="overflow-x-auto">
                <table className="w-full text-[10px] min-w-[300px]">
                  <thead><tr className="text-foreground-400 border-b border-background-100"><th className="text-left py-1">公司</th><th className="text-right py-1">营收增速%</th><th className="text-right py-1">毛利率%</th><th className="text-right py-1">PE</th><th className="text-right py-1">PB</th></tr></thead>
                  <tbody>{detail.relativeValuation.peers.map((p, i) => (
                    <tr key={i} className="border-b border-background-50"><td className="text-foreground-700 py-1.5 font-medium">{p.name}</td>
                      <td className="text-right text-foreground-950 py-1.5">{p.revenueGrowth?.toFixed(1)}</td>
                      <td className="text-right text-foreground-950 py-1.5">{p.grossMargin?.toFixed(1)}</td>
                      <td className="text-right text-foreground-950 py-1.5">{p.pe?.toFixed(1)}</td>
                      <td className="text-right text-foreground-950 py-1.5">{p.pb?.toFixed(2)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ 投资论点 ═══ */}
      {detail?.thesis && (
        <div className="bg-white rounded-xl p-4 border border-background-200/60 mb-4">
          <h2 className="text-base font-bold text-foreground-950 mb-3">投资论点</h2>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#FEF2F2] rounded-xl p-3 border border-red-100">
              <h3 className="text-xs font-semibold text-[#E11D48] mb-2 flex items-center gap-1"><i className="ri-arrow-up-line" /> 看多</h3>
              <ul className="space-y-1">{detail.thesis.bullish.map((b, i) => <li key={i} className="text-[11px] text-foreground-700 leading-relaxed">• {b}</li>)}</ul>
            </div>
            <div className="bg-[#F0FDF4] rounded-xl p-3 border border-green-100">
              <h3 className="text-xs font-semibold text-[#10B981] mb-2 flex items-center gap-1"><i className="ri-arrow-down-line" /> 看空</h3>
              <ul className="space-y-1">{detail.thesis.bearish.map((b, i) => <li key={i} className="text-[11px] text-foreground-700 leading-relaxed">• {b}</li>)}</ul>
            </div>
          </div>
        </div>
      )}

      {/* ═══ 风险提示 ═══ */}
      {detail?.risks?.length ? (
        <div className="bg-white rounded-xl p-4 border border-background-200/60 mb-4">
          <h2 className="text-base font-bold text-foreground-950 mb-3">风险提示</h2>
          <div className="space-y-2">
            {detail.risks.map((r, i) => (
              <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-100">
                <span className="text-amber-500 mt-0.5"><i className="ri-alert-line text-xs" /></span>
                <span className="text-[11px] text-foreground-700">{r}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* 操作按钮 */}
      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => {
            if (report?.status === "生成中" || !report?.detail) {
              setToast("研报生成中，请稍候再导出");
              return;
            }
            window.open(`/reports/${id}/pdf`, "_blank");
          }}
          className="flex items-center justify-center gap-1 flex-1 py-2.5 rounded-xl text-xs font-semibold cursor-pointer transition-colors"
          style={{ background: "#f1f5f9", color: "#64748b" }}>
          <i className="ri-file-pdf-2-line text-sm" />导出 PDF
        </button>
        <button onClick={() => setShowChat(!showChat)}
          className="flex items-center justify-center gap-1 flex-1 py-2.5 rounded-xl text-xs font-semibold cursor-pointer transition-colors"
          style={{ background: showChat ? "#6366f1" : "#f1f5f9", color: showChat ? "#fff" : "#64748b" }}>
          <i className="ri-ai-line text-sm" />AI 追问
        </button>
      </div>

      {/* AI Chat drawer */}
      {showChat && (
        <div className="fixed bottom-0 left-0 w-full z-50 bg-white border-t border-background-200/60 shadow-lg animate-slide-up" style={{ bottom: "80px" }}>
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-background-100">
            <span className="text-xs font-semibold text-foreground-950"><i className="ri-ai-line text-indigo-500" /> 研报追问</span>
            <button onClick={() => setShowChat(false)} className="text-foreground-400 cursor-pointer"><i className="ri-close-line" /></button>
          </div>
          <div className="px-4 py-2 max-h-[180px] overflow-y-auto space-y-2">
            {chatMsgs.length === 0 && <p className="text-[10px] text-foreground-400 text-center py-2">针对报告内容提问，AI 将引用相关章节回答</p>}
            {chatMsgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : ""}`}>
                <div className={m.role === "user" ? "" : "max-w-[80%]"}>
                  <div className={`px-3 py-2 rounded-xl text-[11px] ${m.role === "user" ? "bg-primary-500 text-white" : "bg-background-100 text-foreground-700"}`}>
                    {m.role === "user" ? m.content : stripActions(m.content || "")}
                  </div>
                  {m.role !== "user" && <ActionButtons text={m.content || ""} intent={chatIntent} context={chatMsgs.map(x => x.content).join(" ")} />}
                </div>
              </div>
            ))}
            {chatLoading && <div className="text-[10px] text-foreground-400 animate-pulse">AI 思考中...</div>}
          </div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-t border-background-100">
            <input value={chatInput} onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleChatSend(); } }}
              placeholder="输入问题..." className="flex-1 px-3 py-2 rounded-xl text-xs bg-background-50 text-foreground-950 outline-none border-none" />
            <button onClick={() => handleChatSend()} disabled={!chatInput.trim() || chatLoading}
              className="w-8 h-8 flex items-center justify-center rounded-xl bg-primary-500 text-white cursor-pointer disabled:opacity-40">
              <i className="ri-send-plane-fill text-xs" />
            </button>
          </div>
        </div>
      )}

      {/* Toast */}
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] transition-all duration-300"
        style={{ opacity: toast ? 1 : 0, transform: `translateX(-50%) translateY(${toast ? 0 : -10}px)` }}>
        {toast && <div className="px-4 py-2 rounded-xl text-xs font-medium bg-foreground-800 text-white shadow-lg">{toast}</div>}
      </div>
    </div>
  );
}
